import {Box, Group, Skeleton} from '@mantine/core';
import type {FC} from 'react';

/**
 * Two-column "loading page" placeholder: paired text-line skeletons above a
 * thumbnail + text-line block.
 * @registry Generic full-page loading skeleton (lines + thumbnail block).
 *
 * Ported from `library/elements/PageSkeleton`. DEVIATION: the original took
 * an MUI `sx` prop (typed via `@mui/material`'s `SxProps<Theme>`, which is a
 * banned import in `design/`) and used MUI `Skeleton`'s `animation`
 * (`'wave' | false`) prop. Replaced with a plain `width` prop applied to the
 * outer `Box`, and Mantine `Skeleton`'s boolean `animate` prop (the trailing
 * skeleton in each group keeps `animate={false}` to match the original's
 * static final row).
 */
export interface PageSkeletonProps {
  width?: React.CSSProperties['width'];
}

export const PageSkeleton: FC<PageSkeletonProps> = ({width = '100%'}) => (
  <Box w={width}>
    <Group wrap="nowrap" align="flex-start">
      <Box style={{flex: 1}}>
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} animate={false} />
      </Box>

      <Box style={{flex: 1}}>
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} animate={false} />
      </Box>
    </Group>

    <Group mt="md" align="flex-start" wrap="nowrap">
      <Skeleton height={118} width={210} />
      <Box style={{flex: 1}}>
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} />
        <Skeleton height={8} mt={6} animate={false} />
      </Box>
    </Group>
  </Box>
);
